import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

// ============================================
// Theme Definitions
// ============================================

export type ThemeName = 'crediori-default' | 'classic-blue' | 'ocean-teal' | 'midnight' | 'forest';
export type ColorMode = 'light' | 'dark' | 'system';

export interface ThemeColors {
  // Primary colors
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primary50: string;
  primary100: string;
  primary200: string;
  primary300: string;
  primary400: string;
  primary500: string;
  primary600: string;
  primary700: string;
  primary800: string;
  primary900: string;

  // Accent color
  accent: string;
  accentHover: string;
}

export interface Theme {
  name: ThemeName;
  displayName: string;
  description: string;
  colors: ThemeColors;
}

// ============================================
// Theme Definitions
// ============================================

export const themes: Record<ThemeName, Theme> = {
  'crediori-default': {
    name: 'crediori-default',
    displayName: 'Crediori Default',
    description: 'Modern indigo theme - Trust & Innovation',
    colors: {
      primary: '#4F46E5',
      primaryHover: '#4338CA',
      primaryLight: '#818CF8',
      primary50: '#EEF2FF',
      primary100: '#E0E7FF',
      primary200: '#C7D2FE',
      primary300: '#A5B4FC',
      primary400: '#818CF8',
      primary500: '#6366F1',
      primary600: '#4F46E5',
      primary700: '#4338CA',
      primary800: '#3730A3',
      primary900: '#312E81',
      accent: '#10B981',
      accentHover: '#059669',
    },
  },
  'classic-blue': {
    name: 'classic-blue',
    displayName: 'Classic Blue',
    description: 'Traditional corporate blue theme',
    colors: {
      primary: '#165788',
      primaryHover: '#0d4065',
      primaryLight: '#497593',
      primary50: '#E8F4FC',
      primary100: '#C3DCF4',
      primary200: '#93ACBF',
      primary300: '#497593',
      primary400: '#165788',
      primary500: '#165788',
      primary600: '#004465',
      primary700: '#003852',
      primary800: '#002C40',
      primary900: '#00202E',
      accent: '#249D98',
      accentHover: '#1B7A76',
    },
  },
  'ocean-teal': {
    name: 'ocean-teal',
    displayName: 'Ocean Teal',
    description: 'Fresh and modern teal theme',
    colors: {
      primary: '#0D9488',
      primaryHover: '#0F766E',
      primaryLight: '#2DD4BF',
      primary50: '#F0FDFA',
      primary100: '#CCFBF1',
      primary200: '#99F6E4',
      primary300: '#5EEAD4',
      primary400: '#2DD4BF',
      primary500: '#14B8A6',
      primary600: '#0D9488',
      primary700: '#0F766E',
      primary800: '#115E59',
      primary900: '#134E4A',
      accent: '#6366F1',
      accentHover: '#4F46E5',
    },
  },
  'midnight': {
    name: 'midnight',
    displayName: 'Midnight',
    description: 'Deep blue professional theme',
    colors: {
      primary: '#1E40AF',
      primaryHover: '#1E3A8A',
      primaryLight: '#3B82F6',
      primary50: '#EFF6FF',
      primary100: '#DBEAFE',
      primary200: '#BFDBFE',
      primary300: '#93C5FD',
      primary400: '#60A5FA',
      primary500: '#3B82F6',
      primary600: '#2563EB',
      primary700: '#1D4ED8',
      primary800: '#1E40AF',
      primary900: '#1E3A8A',
      accent: '#F59E0B',
      accentHover: '#D97706',
    },
  },
  'forest': {
    name: 'forest',
    displayName: 'Forest',
    description: 'Natural green theme for sustainability focus',
    colors: {
      primary: '#15803D',
      primaryHover: '#166534',
      primaryLight: '#22C55E',
      primary50: '#F0FDF4',
      primary100: '#DCFCE7',
      primary200: '#BBF7D0',
      primary300: '#86EFAC',
      primary400: '#4ADE80',
      primary500: '#22C55E',
      primary600: '#16A34A',
      primary700: '#15803D',
      primary800: '#166534',
      primary900: '#14532D',
      accent: '#8B5CF6',
      accentHover: '#7C3AED',
    },
  },
};

// ============================================
// Theme Context Types
// ============================================

export interface ThemeContextValue {
  currentTheme: Theme;
  themeName: ThemeName;
  setTheme: (theme: ThemeName) => void;
  availableThemes: Theme[];
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  isDark: boolean;
}

const defaultTheme = themes['crediori-default'];

const ThemeContext = createContext<ThemeContextValue>({
  currentTheme: defaultTheme,
  themeName: 'crediori-default',
  setTheme: () => {},
  availableThemes: Object.values(themes),
  colorMode: 'system',
  setColorMode: () => {},
  isDark: false,
});

// ============================================
// CSS Variable Application
// ============================================

function applyThemeToDOM(theme: Theme) {
  const root = document.documentElement;
  const { colors } = theme;

  // Apply primary colors
  root.style.setProperty('--theme-primary', colors.primary);
  root.style.setProperty('--theme-primary-hover', colors.primaryHover);
  root.style.setProperty('--theme-primary-light', colors.primaryLight);
  root.style.setProperty('--theme-primary-50', colors.primary50);
  root.style.setProperty('--theme-primary-100', colors.primary100);
  root.style.setProperty('--theme-primary-200', colors.primary200);
  root.style.setProperty('--theme-primary-300', colors.primary300);
  root.style.setProperty('--theme-primary-400', colors.primary400);
  root.style.setProperty('--theme-primary-500', colors.primary500);
  root.style.setProperty('--theme-primary-600', colors.primary600);
  root.style.setProperty('--theme-primary-700', colors.primary700);
  root.style.setProperty('--theme-primary-800', colors.primary800);
  root.style.setProperty('--theme-primary-900', colors.primary900);

  // Apply accent colors
  root.style.setProperty('--theme-accent', colors.accent);
  root.style.setProperty('--theme-accent-hover', colors.accentHover);

  // Set theme class for potential CSS overrides
  root.setAttribute('data-theme', theme.name);
}

// Convert hex to HSL for CSS variable compatibility
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyThemeHSLToDOM(theme: Theme) {
  const root = document.documentElement;
  const { colors } = theme;

  // Apply as HSL for shadcn/ui compatibility
  root.style.setProperty('--primary', hexToHSL(colors.primary));
  root.style.setProperty('--primary-foreground', '0 0% 100%');
  root.style.setProperty('--accent', hexToHSL(colors.accent));
  root.style.setProperty('--accent-foreground', '0 0% 100%');
  root.style.setProperty('--ring', hexToHSL(colors.primary));
}

// ============================================
// Dark mode helpers
// ============================================

function getSystemDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveIsDark(mode: ColorMode): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return getSystemDark();
}

function applyDarkClass(isDark: boolean) {
  const root = document.documentElement;
  if (isDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// ============================================
// Theme Provider Component
// ============================================

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeName;
  defaultColorMode?: ColorMode;
  storageKey?: string;
  colorModeStorageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme: initialTheme = 'crediori-default',
  defaultColorMode: initialColorMode = 'system',
  storageKey = 'crediori-theme',
  colorModeStorageKey = 'crediori-color-mode',
}: ThemeProviderProps) {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored && stored in themes) {
        return stored as ThemeName;
      }
    }
    return initialTheme;
  });

  const [colorMode, setColorModeState] = useState<ColorMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(colorModeStorageKey);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    }
    return initialColorMode;
  });

  const [isDark, setIsDark] = useState(() => resolveIsDark(colorMode));

  const currentTheme = themes[themeName];

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (colorMode !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setIsDark(e.matches);
      applyDarkClass(e.matches);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [colorMode]);

  // Apply dark class whenever isDark changes
  useEffect(() => {
    applyDarkClass(isDark);
  }, [isDark]);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyThemeToDOM(currentTheme);
    applyThemeHSLToDOM(currentTheme);
    localStorage.setItem(storageKey, themeName);
  }, [themeName, currentTheme, storageKey]);

  const setTheme = useCallback((newTheme: ThemeName) => {
    if (newTheme in themes) {
      setThemeName(newTheme);
    }
  }, []);

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
    localStorage.setItem(colorModeStorageKey, mode);
    const resolved = resolveIsDark(mode);
    setIsDark(resolved);
    applyDarkClass(resolved);
  }, [colorModeStorageKey]);

  const value: ThemeContextValue = useMemo(() => ({
    currentTheme,
    themeName,
    setTheme,
    availableThemes: Object.values(themes),
    colorMode,
    setColorMode,
    isDark,
  }), [currentTheme, themeName, setTheme, colorMode, setColorMode, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================
// Hook for consuming theme context
// ============================================

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

// ============================================
// Utility: Get theme by name
// ============================================

export function getTheme(name: ThemeName): Theme {
  return themes[name] || defaultTheme;
}
